import { useState, useEffect, useCallback } from "react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
function api(path, opts = {}) {
  const token = localStorage.getItem("sentinel_token");
  return fetch(`${API_URL}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
    body: opts.body,
  }).then(r => r.json());
}

// ── Design tokens ──────────────────────────────────────────
const C = {
  bg:         "#060810",
  surface:    "#0c0f1a",
  surfaceAlt: "#111525",
  card:       "#0f1320",
  border:     "#1a2035",
  borderHi:   "#263050",
  accent:     "#4f8eff",
  accentDim:  "#4f8eff18",
  accentGlow: "#4f8eff35",
  green:      "#00d4aa",
  greenDim:   "#00d4aa18",
  warn:       "#ff9640",
  warnDim:    "#ff964018",
  critical:   "#ff3b6e",
  critDim:    "#ff3b6e18",
  gold:       "#f5c842",
  goldDim:    "#f5c84218",
  purple:     "#a855f7",
  purpleDim:  "#a855f718",
  text:       "#d4dff0",
  textMuted:  "#4a5878",
  textDim:    "#7a90b0",
};

const FONTS = `@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=Outfit:wght@400;500;600;700;800;900&display=swap');`;

const G = `
  ${FONTS}
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{background:${C.bg};color:${C.text};font-family:'Outfit',sans-serif;overflow-x:hidden}
  ::-webkit-scrollbar{width:3px}::-webkit-scrollbar-track{background:${C.surface}}::-webkit-scrollbar-thumb{background:${C.borderHi};border-radius:2px}
  input,select,textarea{background:${C.surfaceAlt};border:1px solid ${C.border};border-radius:6px;color:${C.text};font-family:'Outfit',sans-serif;font-size:13px;padding:9px 13px;outline:none;transition:border-color .18s;width:100%}
  input:focus,select:focus,textarea:focus{border-color:${C.accent}}
  input::placeholder{color:${C.textMuted}}
  @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
  @keyframes slideIn{from{transform:translateX(100%);opacity:0}to{transform:translateX(0);opacity:1}}
  .fade{animation:fadeUp .35s ease both}
  .mono{font-family:'IBM Plex Mono',monospace}
`;

// ── Shared components ──────────────────────────────────────
const Pill = ({ children, color = C.accent }) => (
  <span style={{ display:"inline-flex",alignItems:"center",background:color+"18",color,border:`1px solid ${color}35`,borderRadius:4,padding:"2px 8px",fontSize:10,fontWeight:700,fontFamily:"'IBM Plex Mono',monospace",textTransform:"uppercase",letterSpacing:1,whiteSpace:"nowrap" }}>
    {children}
  </span>
);
const Dot = ({ color }) => <span style={{ display:"inline-block",width:7,height:7,borderRadius:"50%",background:color,boxShadow:`0 0 6px ${color}`,marginRight:6,animation:"pulse 2s infinite" }} />;
const Spin = ({ s=16 }) => <div style={{ width:s,height:s,border:`2px solid ${C.border}`,borderTopColor:C.accent,borderRadius:"50%",animation:"spin .65s linear infinite" }} />;

function Btn({ children, variant="primary", onClick, disabled, loading, style={}, size="md" }) {
  const p = size==="sm"?"6px 14px":size==="lg"?"13px 28px":"9px 20px";
  const f = size==="sm"?12:size==="lg"?15:13;
  const v = {
    primary:{ background:C.accent, color:"#fff" },
    success:{ background:C.green,  color:C.bg },
    ghost:  { background:"transparent", color:C.textDim, border:`1px solid ${C.border}` },
    danger: { background:C.critDim, color:C.critical, border:`1px solid ${C.critical}40` },
    warn:   { background:C.warnDim, color:C.warn,     border:`1px solid ${C.warn}40` },
    purple: { background:C.purpleDim, color:C.purple, border:`1px solid ${C.purple}40` },
  };
  return (
    <button onClick={!disabled&&!loading?onClick:undefined} style={{ display:"inline-flex",alignItems:"center",gap:7,padding:p,borderRadius:6,border:"none",fontFamily:"'Outfit',sans-serif",fontWeight:600,fontSize:f,cursor:disabled||loading?"not-allowed":"pointer",opacity:disabled||loading?.5:1,transition:"all .15s,opacity .15s",letterSpacing:.2,...v[variant],...style }}>
      {loading?<Spin s={f} />:null}{children}
    </button>
  );
}

function Card({ children, style={}, accent=null }) {
  return (
    <div style={{ background:C.card,border:`1px solid ${accent?accent+"45":C.border}`,borderRadius:10,padding:20,boxShadow:accent?`0 0 24px ${accent}20`:"none",...style }}>
      {children}
    </div>
  );
}

function StatCard({ label, value, sub, color=C.accent, icon, delta }) {
  return (
    <Card style={{ position:"relative",overflow:"hidden" }}>
      <div style={{ position:"absolute",top:16,right:16,fontSize:26,opacity:.12,color }}>{icon}</div>
      <div style={{ fontSize:11,color:C.textMuted,fontFamily:"'IBM Plex Mono',monospace",letterSpacing:1,marginBottom:6 }}>{label.toUpperCase()}</div>
      <div style={{ fontSize:36,fontWeight:900,color,fontFamily:"'IBM Plex Mono',monospace",lineHeight:1,marginBottom:6 }}>{value}</div>
      {sub  && <div style={{ fontSize:12,color:C.textDim }}>{sub}</div>}
      {delta!==undefined && <div style={{ fontSize:11,color:delta>=0?C.green:C.critical,marginTop:4 }}>{delta>=0?"↑":"↓"} {Math.abs(delta)}% vs last month</div>}
      <div style={{ position:"absolute",bottom:0,left:0,right:0,height:2,background:`linear-gradient(90deg,${color}88,transparent)` }} />
    </Card>
  );
}

function Avatar({ name, size=32 }) {
  const colors = [C.accent,C.green,C.purple,C.warn,C.gold];
  const idx    = (name||"?").charCodeAt(0) % colors.length;
  return (
    <div style={{ width:size,height:size,borderRadius:"50%",background:colors[idx]+"25",border:`1px solid ${colors[idx]}50`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*.38,fontWeight:700,color:colors[idx],flexShrink:0 }}>
      {(name||"?")[0].toUpperCase()}
    </div>
  );
}

// ── Toast notification ─────────────────────────────────────
function Toast({ msg, type="success", onClose }) {
  useEffect(() => { const t=setTimeout(onClose,3200); return ()=>clearTimeout(t); }, []);
  const c = type==="success"?C.green:type==="error"?C.critical:C.warn;
  return (
    <div style={{ position:"fixed",top:20,right:20,zIndex:9999,background:C.card,border:`1px solid ${c}50`,borderLeft:`3px solid ${c}`,borderRadius:8,padding:"12px 18px",fontSize:13,color:C.text,animation:"slideIn .3s ease",display:"flex",gap:12,alignItems:"center",boxShadow:"0 8px 32px #00000088",minWidth:260 }}>
      <span style={{ color:c }}>{type==="success"?"✓":type==="error"?"✕":"!"}</span>
      {msg}
      <button onClick={onClose} style={{ marginLeft:"auto",background:"none",border:"none",color:C.textMuted,cursor:"pointer",fontSize:16 }}>×</button>
    </div>
  );
}

// ── Modal ──────────────────────────────────────────────────
function Modal({ title, children, onClose, width=520 }) {
  return (
    <div style={{ position:"fixed",inset:0,background:"#00000090",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20 }} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:C.card,border:`1px solid ${C.borderHi}`,borderRadius:12,width:"100%",maxWidth:width,maxHeight:"90vh",overflowY:"auto",animation:"fadeUp .25s ease" }}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"18px 24px",borderBottom:`1px solid ${C.border}` }}>
          <div style={{ fontWeight:700,fontSize:16 }}>{title}</div>
          <button onClick={onClose} style={{ background:"none",border:"none",color:C.textMuted,cursor:"pointer",fontSize:20,lineHeight:1 }}>×</button>
        </div>
        <div style={{ padding:24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ display:"block",fontSize:11,color:C.textMuted,fontFamily:"'IBM Plex Mono',monospace",letterSpacing:1,textTransform:"uppercase",marginBottom:5 }}>
        {label}{required&&<span style={{ color:C.critical }}> *</span>}
      </label>
      {children}
    </div>
  );
}

const genId = () => Math.random().toString(36).slice(2,10);

function fmtLastScan(ts) {
  if (!ts) return "Never";
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 2)   return "Just now";
  if (m < 60)  return `${m}min ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function normalizeClient(u) {
  return {
    id:        u.id,
    company:   u.company_name || u.email,
    contact:   u.full_name    || "—",
    email:     u.email,
    phone:     u.phone || "—",
    plan:      u.plan_slug    || "trial",
    status:    u.sub_status   || "trial",
    targets:   parseInt(u.target_count) || 0,
    scans:     parseInt(u.scan_count)   || 0,
    vulns:     parseInt(u.vuln_count)   || 0,
    revenue:   parseFloat(u.revenue)    || 0,
    joined:    u.joined ? u.joined.split("T")[0] : "—",
    lastScan:  fmtLastScan(u.last_scan),
    riskScore: parseFloat(u.risk_score) || 0,
    logo:      (u.company_name || u.email || "?")[0].toUpperCase(),
  };
}

const PLAN_COLORS = { starter:"#94a3b8", business:C.accent, enterprise:C.gold, trial:C.purple };
const STATUS_COLORS = { active:C.green, trial:C.purple, past_due:C.warn, cancelled:C.critical, expired:C.textMuted };
const RISK_COLOR = s => s>=9?C.critical:s>=7?C.warn:s>=4?C.gold:C.green;

const ADMIN_NAV = [
  { id:"overview",    icon:"▦", label:"Overview"      },
  { id:"clients",     icon:"⊞", label:"Clients"       },
  { id:"scans",       icon:"◎", label:"Scan Monitor"  },
  { id:"revenue",     icon:"◈", label:"Revenue"       },
  { id:"plans",       icon:"⊟", label:"Plans"         },
  { id:"branding",    icon:"⬡", label:"White-Label"   },
  { id:"system",      icon:"⊙", label:"System"        },
];

// ══════════════════════════════════════════════════════════════
//  OVERVIEW
// ══════════════════════════════════════════════════════════════
function Overview({ clients, stats, onNav }) {
  const active   = stats?.active_subscriptions ?? clients.filter(c=>c.status==="active").length;
  const revenue  = stats?.total_revenue_zar    ?? clients.reduce((s,c)=>s+c.revenue,0);
  const vulns    = stats?.open_vulns           ?? clients.reduce((s,c)=>s+c.vulns,0);
  const scans    = stats?.total_scans          ?? clients.reduce((s,c)=>s+c.scans,0);
  const pastDue  = clients.filter(c=>c.status==="past_due");
  const critical = clients.filter(c=>c.riskScore>=9);

  return (
    <div className="fade">
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontSize:24,fontWeight:800,marginBottom:4 }}>Admin Overview</h1>
        <div style={{ fontSize:13,color:C.textMuted }}>Platform health · {new Date().toLocaleDateString("en-ZA",{weekday:"long",year:"numeric",month:"long",day:"numeric"})}</div>
      </div>

      <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:24 }}>
        <StatCard label="Active Clients"   value={active}                             color={C.green}    icon="⊞" />
        <StatCard label="Total Revenue"    value={`R${Math.round(revenue).toLocaleString()}`} color={C.gold} icon="◈" />
        <StatCard label="Total Scans"      value={scans}                              color={C.accent}   icon="◎" />
        <StatCard label="Open Vulns"       value={vulns}                              color={C.critical} icon="⚠" />
      </div>

      {/* Alerts */}
      {(pastDue.length > 0 || critical.length > 0) && (
        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:24 }}>
          {pastDue.length > 0 && (
            <Card accent={C.warn}>
              <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:12 }}>
                <Dot color={C.warn} /><span style={{ fontWeight:700,fontSize:14,color:C.warn }}>Payment Issues</span>
                <Pill color={C.warn}>{pastDue.length}</Pill>
              </div>
              {pastDue.map(c=>(
                <div key={c.id} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${C.border}22` }}>
                  <div style={{ display:"flex",alignItems:"center",gap:8 }}><Avatar name={c.company} size={28} /><div style={{ fontSize:13 }}>{c.company}</div></div>
                  <Pill color={C.warn}>Past Due</Pill>
                </div>
              ))}
            </Card>
          )}
          {critical.length > 0 && (
            <Card accent={C.critical}>
              <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:12 }}>
                <Dot color={C.critical} /><span style={{ fontWeight:700,fontSize:14,color:C.critical }}>Critical Risk Clients</span>
                <Pill color={C.critical}>{critical.length}</Pill>
              </div>
              {critical.map(c=>(
                <div key={c.id} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${C.border}22` }}>
                  <div style={{ display:"flex",alignItems:"center",gap:8 }}><Avatar name={c.company} size={28} /><div style={{ fontSize:13 }}>{c.company}</div></div>
                  <span style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:16,fontWeight:700,color:C.critical }}>{c.riskScore}</span>
                </div>
              ))}
            </Card>
          )}
        </div>
      )}

      {/* Plan distribution */}
      <div style={{ display:"grid",gridTemplateColumns:"1fr 2fr",gap:14,marginBottom:24 }}>
        <Card>
          <div style={{ fontWeight:700,fontSize:14,marginBottom:16 }}>Plan Distribution</div>
          {["starter","business","enterprise"].map(p => {
            const n = clients.filter(c=>c.plan===p).length;
            return (
              <div key={p} style={{ marginBottom:14 }}>
                <div style={{ display:"flex",justifyContent:"space-between",marginBottom:5 }}>
                  <span style={{ fontSize:13,textTransform:"capitalize",fontWeight:600,color:PLAN_COLORS[p] }}>{p}</span>
                  <span style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:PLAN_COLORS[p] }}>{n}</span>
                </div>
                <div style={{ height:4,background:C.border,borderRadius:2 }}>
                  <div style={{ height:"100%",width:`${(n/clients.length)*100}%`,background:PLAN_COLORS[p],borderRadius:2,transition:"width .6s" }} />
                </div>
              </div>
            );
          })}
          <div style={{ marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`,fontSize:12,color:C.textMuted }}>
            Trial: <span style={{ color:C.purple }}>{clients.filter(c=>c.status==="trial").length}</span>
          </div>
        </Card>

        <Card>
          <div style={{ fontWeight:700,fontSize:14,marginBottom:16 }}>Top Clients by Revenue</div>
          {[...clients].sort((a,b)=>b.revenue-a.revenue).slice(0,5).map((c,i)=>(
            <div key={c.id} style={{ display:"flex",alignItems:"center",gap:12,padding:"9px 0",borderBottom:`1px solid ${C.border}22` }}>
              <span style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:C.textMuted,width:18 }}>#{i+1}</span>
              <Avatar name={c.company} size={30} />
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13,fontWeight:600,marginBottom:2 }}>{c.company}</div>
                <Pill color={PLAN_COLORS[c.plan]}>{c.plan}</Pill>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:15,fontWeight:700,color:C.gold }}>R{c.revenue.toLocaleString()}</div>
                <div style={{ fontSize:10,color:C.textMuted }}>/ month</div>
              </div>
            </div>
          ))}
        </Card>
      </div>

      {/* Recent activity */}
      <Card>
        <div style={{ fontWeight:700,fontSize:14,marginBottom:16 }}>Recent Platform Activity</div>
        {[
          { time:"2min ago",  event:"Scan completed",        detail:"Acme Corp — Web Server 01 · Risk 8.4",     color:C.green  },
          { time:"14min ago", event:"New client registered",  detail:"Bright Spark Media — Trial plan started", color:C.purple },
          { time:"1h ago",    event:"Critical vuln detected", detail:"Nexus Retail — CVE-2021-44228 Log4Shell", color:C.critical},
          { time:"2h ago",    event:"Payment received",       detail:"Cape Cloud Hosting — R4,999 Enterprise",  color:C.gold   },
          { time:"3h ago",    event:"Report downloaded",      detail:"TechFlow Systems — Executive Summary PDF",color:C.accent },
          { time:"5h ago",    event:"Subscription cancelled", detail:"Client ID c-0041 — Starter plan",        color:C.warn   },
        ].map((a,i)=>(
          <div key={i} style={{ display:"flex",alignItems:"flex-start",gap:14,padding:"10px 0",borderBottom:`1px solid ${C.border}22` }}>
            <div style={{ width:8,height:8,borderRadius:"50%",background:a.color,marginTop:5,flexShrink:0 }} />
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:600,fontSize:13 }}>{a.event}</div>
              <div style={{ fontSize:12,color:C.textDim,marginTop:2 }}>{a.detail}</div>
            </div>
            <div style={{ fontSize:11,color:C.textMuted,whiteSpace:"nowrap" }}>{a.time}</div>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  CLIENTS
// ══════════════════════════════════════════════════════════════
function Clients({ clients, setClients, toast, updateClientStatus, sendResetEmail }) {
  const [search, setSearch]   = useState("");
  const [planF, setPlanF]     = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newClient, setNewClient] = useState({ company:"",contact:"",email:"",phone:"",plan:"starter" });

  const filtered = clients.filter(c => {
    const q = search.toLowerCase();
    const matchQ = !q || c.company.toLowerCase().includes(q) || c.email.toLowerCase().includes(q) || c.contact.toLowerCase().includes(q);
    const matchP = planF==="all"   || c.plan===planF;
    const matchS = statusF==="all" || c.status===statusF;
    return matchQ && matchP && matchS;
  });

  const addClient = () => {
    if (!newClient.company || !newClient.email) return;
    const client = { ...newClient, id:"c"+genId(), status:"trial", targets:0, scans:0, vulns:0, revenue:0, joined:new Date().toISOString().split("T")[0], lastScan:"Never", riskScore:0, logo:(newClient.company[0]||"?").toUpperCase() };
    setClients(p=>[client,...p]);
    setShowAdd(false);
    setNewClient({ company:"",contact:"",email:"",phone:"",plan:"starter" });
    toast("Client added — note: this is local only until they register");
  };

  const updateStatus = async (id, status) => {
    await updateClientStatus(id, status);
    setSelected(null);
  };

  const impersonate = (c) => { toast(`Viewing as ${c.company} — demo mode`); };

  return (
    <div className="fade">
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:24,fontWeight:800,marginBottom:4 }}>Clients</h1>
          <div style={{ fontSize:13,color:C.textMuted }}>{filtered.length} of {clients.length} clients</div>
        </div>
        <Btn variant="primary" onClick={()=>setShowAdd(true)}>+ Add Client</Btn>
      </div>

      {/* Filters */}
      <div style={{ display:"flex",gap:10,marginBottom:20,flexWrap:"wrap" }}>
        <input placeholder="Search clients, email, contact..." value={search} onChange={e=>setSearch(e.target.value)} style={{ width:280 }} />
        <select value={planF} onChange={e=>setPlanF(e.target.value)} style={{ width:140 }}>
          <option value="all">All Plans</option>
          <option value="starter">Starter</option>
          <option value="business">Business</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <select value={statusF} onChange={e=>setStatusF(e.target.value)} style={{ width:140 }}>
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="trial">Trial</option>
          <option value="past_due">Past Due</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Table */}
      <Card style={{ padding:0,overflow:"hidden" }}>
        <table style={{ width:"100%",borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ background:C.surfaceAlt }}>
              {["Client","Contact","Plan","Status","Targets","Risk","Revenue","Last Scan","Actions"].map(h=>(
                <th key={h} style={{ padding:"11px 16px",fontSize:10,color:C.textMuted,fontFamily:"'IBM Plex Mono',monospace",letterSpacing:1,textAlign:"left",fontWeight:600,whiteSpace:"nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((c,i)=>(
              <tr key={c.id} style={{ borderBottom:`1px solid ${C.border}22`,transition:"background .1s",cursor:"pointer" }}
                onMouseEnter={e=>e.currentTarget.style.background=C.surfaceAlt}
                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <td style={{ padding:"12px 16px" }}>
                  <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                    <Avatar name={c.company} size={32} />
                    <div>
                      <div style={{ fontWeight:600,fontSize:13 }}>{c.company}</div>
                      <div style={{ fontSize:11,color:C.textMuted,fontFamily:"'IBM Plex Mono',monospace" }}>#{c.id}</div>
                    </div>
                  </div>
                </td>
                <td style={{ padding:"12px 16px" }}>
                  <div style={{ fontSize:13 }}>{c.contact}</div>
                  <div style={{ fontSize:11,color:C.textMuted }}>{c.email}</div>
                </td>
                <td style={{ padding:"12px 16px" }}><Pill color={PLAN_COLORS[c.plan]}>{c.plan}</Pill></td>
                <td style={{ padding:"12px 16px" }}>
                  <div style={{ display:"flex",alignItems:"center" }}>
                    <Dot color={STATUS_COLORS[c.status]||C.textMuted} />
                    <span style={{ fontSize:12,color:STATUS_COLORS[c.status],fontWeight:600,textTransform:"capitalize" }}>{c.status.replace("_"," ")}</span>
                  </div>
                </td>
                <td style={{ padding:"12px 16px",fontFamily:"'IBM Plex Mono',monospace",fontSize:13,color:C.textDim }}>{c.targets}</td>
                <td style={{ padding:"12px 16px" }}>
                  <span style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:15,fontWeight:700,color:RISK_COLOR(c.riskScore) }}>{c.riskScore}</span>
                </td>
                <td style={{ padding:"12px 16px",fontFamily:"'IBM Plex Mono',monospace",fontSize:13,color:C.gold,fontWeight:700 }}>
                  {c.revenue>0?`R${c.revenue.toLocaleString()}`:"—"}
                </td>
                <td style={{ padding:"12px 16px",fontSize:12,color:C.textMuted }}>{c.lastScan}</td>
                <td style={{ padding:"12px 16px" }}>
                  <div style={{ display:"flex",gap:5 }}>
                    <Btn variant="ghost" size="sm" onClick={()=>setSelected(c)}>View</Btn>
                    <Btn variant="purple" size="sm" onClick={()=>impersonate(c)}>Login As</Btn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length===0&&<div style={{ padding:40,textAlign:"center",color:C.textMuted,fontSize:14 }}>No clients match your filters.</div>}
      </Card>

      {/* Add client modal */}
      {showAdd && (
        <Modal title="Add New Client" onClose={()=>setShowAdd(false)}>
          <Field label="Company Name" required><input placeholder="Acme Corporation" value={newClient.company} onChange={e=>setNewClient(p=>({...p,company:e.target.value}))} /></Field>
          <Field label="Contact Person"><input placeholder="John Smith" value={newClient.contact} onChange={e=>setNewClient(p=>({...p,contact:e.target.value}))} /></Field>
          <Field label="Email Address" required><input type="email" placeholder="john@acme.co.za" value={newClient.email} onChange={e=>setNewClient(p=>({...p,email:e.target.value}))} /></Field>
          <Field label="Phone Number"><input placeholder="+27 82 000 0000" value={newClient.phone} onChange={e=>setNewClient(p=>({...p,phone:e.target.value}))} /></Field>
          <Field label="Starting Plan">
            <select value={newClient.plan} onChange={e=>setNewClient(p=>({...p,plan:e.target.value}))}>
              <option value="starter">Starter — R499/mo</option>
              <option value="business">Business — R1,499/mo</option>
              <option value="enterprise">Enterprise — R4,999/mo</option>
            </select>
          </Field>
          <div style={{ display:"flex",gap:8,marginTop:8 }}>
            <Btn variant="primary" onClick={addClient} style={{ flex:1 }}>Add Client</Btn>
            <Btn variant="ghost" onClick={()=>setShowAdd(false)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* Client detail modal */}
      {selected && (
        <Modal title={selected.company} onClose={()=>setSelected(null)} width={640}>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20 }}>
            {[
              ["Email",    selected.email],
              ["Phone",    selected.phone],
              ["Plan",     selected.plan],
              ["Status",   selected.status],
              ["Joined",   selected.joined],
              ["Targets",  selected.targets],
              ["Total Scans",selected.scans],
              ["Risk Score",`${selected.riskScore}/10`],
            ].map(([k,v])=>(
              <div key={k} style={{ background:C.surfaceAlt,borderRadius:7,padding:"10px 14px" }}>
                <div style={{ fontSize:11,color:C.textMuted,marginBottom:3 }}>{k}</div>
                <div style={{ fontSize:13,fontWeight:600 }}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{ fontWeight:700,fontSize:13,marginBottom:10 }}>Quick Actions</div>
          <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
            <Btn variant="success" size="sm" onClick={()=>updateStatus(selected.id,"active")}>✓ Activate</Btn>
            <Btn variant="warn"    size="sm" onClick={()=>updateStatus(selected.id,"past_due")}>Mark Past Due</Btn>
            <Btn variant="danger"  size="sm" onClick={()=>updateStatus(selected.id,"cancelled")}>Cancel Sub</Btn>
            <Btn variant="ghost"   size="sm" onClick={()=>sendResetEmail(selected)}>Reset Password</Btn>
            <Btn variant="purple"  size="sm" onClick={()=>impersonate(selected)}>Login As Client</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  SCAN MONITOR
// ══════════════════════════════════════════════════════════════
function ScanMonitor({ allScans, loading }) {
  const scans = allScans;
  const statusColor = { running:C.accent, completed:C.green, queued:C.textMuted, failed:C.critical };

  return (
    <div className="fade">
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontSize:24,fontWeight:800,marginBottom:4 }}>Scan Monitor</h1>
        <div style={{ fontSize:13,color:C.textMuted }}>Live view of all platform scans</div>
      </div>

      <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:24 }}>
        {[["Running",  scans.filter(s=>s.status==="running").length,  C.accent  ],
          ["Completed",scans.filter(s=>s.status==="completed").length,C.green   ],
          ["Queued",   scans.filter(s=>s.status==="queued").length,   C.textMuted],
          ["Failed",   scans.filter(s=>s.status==="failed").length,   C.critical],
        ].map(([l,v,c])=>(
          <Card key={l}>
            <div style={{ fontSize:11,color:C.textMuted,fontFamily:"'IBM Plex Mono',monospace",letterSpacing:1,marginBottom:6 }}>{l.toUpperCase()}</div>
            <div style={{ fontSize:40,fontWeight:900,color:c,fontFamily:"'IBM Plex Mono',monospace" }}>{loading?"…":v}</div>
          </Card>
        ))}
      </div>

      <Card style={{ padding:0,overflow:"hidden" }}>
        <table style={{ width:"100%",borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ background:C.surfaceAlt }}>
              {["Client","Target","Type","Status","Started","Vulns","Risk"].map(h=>(
                <th key={h} style={{ padding:"11px 16px",fontSize:10,color:C.textMuted,fontFamily:"'IBM Plex Mono',monospace",letterSpacing:1,textAlign:"left",fontWeight:600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} style={{ padding:40,textAlign:"center",color:C.textMuted }}>Loading scans…</td></tr>
            )}
            {!loading && scans.length === 0 && (
              <tr><td colSpan={7} style={{ padding:40,textAlign:"center",color:C.textMuted }}>No scans yet.</td></tr>
            )}
            {!loading && scans.map(s=>(
              <tr key={s.id} style={{ borderBottom:`1px solid ${C.border}22` }}>
                <td style={{ padding:"12px 16px",fontSize:13,fontWeight:600 }}>{s.company_name || s.user_email}</td>
                <td style={{ padding:"12px 16px",fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:C.accent }}>
                  {s.ip_address || s.hostname || s.target_name}
                </td>
                <td style={{ padding:"12px 16px" }}><Pill color={C.textMuted}>{s.scan_type}</Pill></td>
                <td style={{ padding:"12px 16px" }}>
                  <div style={{ display:"flex",alignItems:"center" }}>
                    {s.status==="running"&&<Dot color={C.accent} />}
                    <span style={{ color:statusColor[s.status]||C.textMuted,fontSize:12,fontWeight:600,textTransform:"capitalize" }}>{s.status}</span>
                  </div>
                </td>
                <td style={{ padding:"12px 16px",fontSize:12,color:C.textMuted,fontFamily:"'IBM Plex Mono',monospace" }}>
                  {s.started_at ? new Date(s.started_at).toLocaleTimeString("en-ZA",{hour:"2-digit",minute:"2-digit"}) : "—"}
                </td>
                <td style={{ padding:"12px 16px",fontFamily:"'IBM Plex Mono',monospace",fontSize:13 }}>{s.total_vulns ?? "—"}</td>
                <td style={{ padding:"12px 16px" }}>
                  {s.risk_score ? <span style={{ fontFamily:"'IBM Plex Mono',monospace",fontWeight:700,color:RISK_COLOR(s.risk_score) }}>{s.risk_score}</span> : <span style={{ color:C.textMuted }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  REVENUE
// ══════════════════════════════════════════════════════════════
function Revenue({ clients, stats }) {
  const months = ["Nov","Dec","Jan","Feb","Mar","Apr","May"];
  const data   = [6498,9496,11493,14491,15988,19984,21479];
  const maxVal = Math.max(...data);

  const totalRev = stats?.total_revenue_zar ?? clients.reduce((s,c)=>s+c.revenue,0);
  const mrr      = clients.reduce((s,c)=>s+c.revenue,0);
  const arr      = totalRev * 12;
  const active   = clients.filter(c=>c.status==="active").length;
  const arpu     = active>0?Math.round(mrr/active):0;
  const churn    = 0;

  return (
    <div className="fade">
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontSize:24,fontWeight:800,marginBottom:4 }}>Revenue</h1>
        <div style={{ fontSize:13,color:C.textMuted }}>PayFast subscription analytics</div>
      </div>

      <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:24 }}>
        <StatCard label="Total Revenue" value={`R${Math.round(totalRev).toLocaleString()}`} color={C.gold}   icon="◈" sub="All time (PayFast)" />
        <StatCard label="ARR Estimate"  value={`R${(arr/1000).toFixed(0)}k`}               color={C.green}  icon="⬡" sub="Annual Run Rate" />
        <StatCard label="ARPU"          value={`R${arpu.toLocaleString()}`}                 color={C.accent} icon="⊞" sub="Avg Revenue / Active Client" />
        <StatCard label="Active Subs"   value={active}                                      color={C.warn}   icon="⊟" sub="Paying subscribers" />
      </div>

      {/* Bar chart */}
      <Card style={{ marginBottom:16 }}>
        <div style={{ fontWeight:700,fontSize:14,marginBottom:20 }}>Monthly Revenue (ZAR)</div>
        <div style={{ display:"flex",gap:12,alignItems:"flex-end",height:180 }}>
          {data.map((v,i)=>(
            <div key={i} style={{ flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:6 }}>
              <div style={{ fontSize:11,color:C.textDim,fontFamily:"'IBM Plex Mono',monospace" }}>R{(v/1000).toFixed(0)}k</div>
              <div style={{ width:"100%",background:`linear-gradient(180deg,${C.accent},${C.accent}88)`,borderRadius:"4px 4px 0 0",height:`${(v/maxVal)*140}px`,transition:"height .6s",boxShadow:i===data.length-1?`0 0 16px ${C.accentGlow}`:"none" }} />
              <div style={{ fontSize:11,color:C.textMuted }}>{months[i]}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Revenue by plan */}
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14 }}>
        <Card>
          <div style={{ fontWeight:700,fontSize:14,marginBottom:16 }}>Revenue by Plan</div>
          {["starter","business","enterprise"].map(p=>{
            const rev = clients.filter(c=>c.plan===p).reduce((s,c)=>s+c.revenue,0);
            return (
              <div key={p} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${C.border}22` }}>
                <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                  <div style={{ width:8,height:8,borderRadius:"50%",background:PLAN_COLORS[p] }} />
                  <span style={{ fontSize:13,textTransform:"capitalize",fontWeight:600 }}>{p}</span>
                </div>
                <span style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:14,fontWeight:700,color:PLAN_COLORS[p] }}>R{rev.toLocaleString()}</span>
              </div>
            );
          })}
        </Card>

        <Card>
          <div style={{ fontWeight:700,fontSize:14,marginBottom:16 }}>Recent Transactions</div>
          {[
            { client:"Cape Cloud",   amount:4999,plan:"enterprise",date:"Today" },
            { client:"TechFlow",     amount:4999,plan:"enterprise",date:"Today" },
            { client:"Nexus Retail", amount:1499,plan:"business",  date:"Today" },
            { client:"Acme Corp",    amount:1499,plan:"business",  date:"Today" },
            { client:"SafeGuard",    amount:499, plan:"starter",   date:"Yesterday" },
          ].map((t,i)=>(
            <div key={i} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${C.border}22` }}>
              <div>
                <div style={{ fontSize:13,fontWeight:600,marginBottom:2 }}>{t.client}</div>
                <Pill color={PLAN_COLORS[t.plan]}>{t.plan}</Pill>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:14,fontWeight:700,color:C.gold }}>R{t.amount.toLocaleString()}</div>
                <div style={{ fontSize:11,color:C.textMuted }}>{t.date}</div>
              </div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  PLANS MANAGER
// ══════════════════════════════════════════════════════════════
function PlansManager({ toast }) {
  const [plans, setPlans] = useState([
    { id:"p1",name:"Starter",    slug:"starter",    price:499,  targets:5,  scans:10, active:true  },
    { id:"p2",name:"Business",   slug:"business",   price:1499, targets:25, scans:50, active:true  },
    { id:"p3",name:"Enterprise", slug:"enterprise", price:4999, targets:-1, scans:-1, active:true  },
  ]);
  const [editing, setEditing] = useState(null);

  const save = () => {
    setPlans(p=>p.map(pl=>pl.id===editing.id?editing:pl));
    setEditing(null);
    toast("Plan updated");
  };

  return (
    <div className="fade">
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontSize:24,fontWeight:800,marginBottom:4 }}>Plans Manager</h1>
        <div style={{ fontSize:13,color:C.textMuted }}>Configure pricing tiers and limits</div>
      </div>

      <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16 }}>
        {plans.map(p=>(
          <Card key={p.id} accent={p.name==="Business"?C.accent:null}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16 }}>
              <Pill color={PLAN_COLORS[p.slug]}>{p.name}</Pill>
              <label style={{ display:"flex",alignItems:"center",gap:6,cursor:"pointer" }}>
                <input type="checkbox" checked={p.active} onChange={e=>setPlans(pl=>pl.map(x=>x.id===p.id?{...x,active:e.target.checked}:x))} style={{ width:"auto",accentColor:C.accent }} />
                <span style={{ fontSize:12,color:C.textMuted }}>Active</span>
              </label>
            </div>
            <div style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:40,fontWeight:700,color:PLAN_COLORS[p.slug],marginBottom:4 }}>R{p.price}</div>
            <div style={{ fontSize:12,color:C.textMuted,marginBottom:16 }}>/ month</div>
            <div style={{ fontSize:13,color:C.textDim,marginBottom:4 }}>Targets: <strong style={{ color:C.text }}>{p.targets===-1?"Unlimited":p.targets}</strong></div>
            <div style={{ fontSize:13,color:C.textDim,marginBottom:20 }}>Scans/mo: <strong style={{ color:C.text }}>{p.scans===-1?"Unlimited":p.scans}</strong></div>
            <Btn variant="ghost" size="sm" onClick={()=>setEditing({...p})} style={{ width:"100%" }}>Edit Plan</Btn>
          </Card>
        ))}
      </div>

      {editing && (
        <Modal title={`Edit ${editing.name} Plan`} onClose={()=>setEditing(null)}>
          <Field label="Plan Name"><input value={editing.name} onChange={e=>setEditing(p=>({...p,name:e.target.value}))} /></Field>
          <Field label="Monthly Price (ZAR)"><input type="number" value={editing.price} onChange={e=>setEditing(p=>({...p,price:parseInt(e.target.value)||0}))} /></Field>
          <Field label="Max Targets (-1 = unlimited)"><input type="number" value={editing.targets} onChange={e=>setEditing(p=>({...p,targets:parseInt(e.target.value)||0}))} /></Field>
          <Field label="Max Scans/Month (-1 = unlimited)"><input type="number" value={editing.scans} onChange={e=>setEditing(p=>({...p,scans:parseInt(e.target.value)||0}))} /></Field>
          <div style={{ padding:"10px 14px",background:C.warnDim,border:`1px solid ${C.warn}40`,borderRadius:6,fontSize:12,color:C.warn,marginBottom:16 }}>
            ⚠ Price changes only apply to new subscriptions. Existing clients keep their current pricing until they upgrade.
          </div>
          <div style={{ display:"flex",gap:8 }}>
            <Btn variant="primary" onClick={save} style={{ flex:1 }}>Save Changes</Btn>
            <Btn variant="ghost" onClick={()=>setEditing(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  WHITE LABEL
// ══════════════════════════════════════════════════════════════
function WhiteLabel({ toast }) {
  const [brand, setBrand] = useState({
    appName:"Sentinel", tagline:"Professional PenTest Suite", primaryColor:"#4f8eff",
    accentColor:"#00d4aa", logo:"⬡", supportEmail:"support@yourapp.co.za",
    footerText:"© 2026 Your Company. Confidential.",
    reportHeader:"Security Assessment Report", reportLogo:true,
  });
  const [saved, setSaved] = useState(false);

  const save = () => { toast("Branding saved and applied"); setSaved(true); setTimeout(()=>setSaved(false),2500); };

  return (
    <div className="fade">
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontSize:24,fontWeight:800,marginBottom:4 }}>White-Label Branding</h1>
        <div style={{ fontSize:13,color:C.textMuted }}>Customise the platform and reports with your brand</div>
      </div>

      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:16 }}>
        <div>
          <Card style={{ marginBottom:16 }}>
            <div style={{ fontWeight:700,fontSize:15,marginBottom:20 }}>Platform Branding</div>
            <Field label="App / Product Name"><input value={brand.appName} onChange={e=>setBrand(p=>({...p,appName:e.target.value}))} /></Field>
            <Field label="Tagline"><input value={brand.tagline} onChange={e=>setBrand(p=>({...p,tagline:e.target.value}))} /></Field>
            <Field label="Logo Icon / Emoji"><input value={brand.logo} onChange={e=>setBrand(p=>({...p,logo:e.target.value}))} /></Field>
            <Field label="Support Email"><input value={brand.supportEmail} onChange={e=>setBrand(p=>({...p,supportEmail:e.target.value}))} /></Field>
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
              <Field label="Primary Color">
                <div style={{ display:"flex",gap:8,alignItems:"center" }}>
                  <input type="color" value={brand.primaryColor} onChange={e=>setBrand(p=>({...p,primaryColor:e.target.value}))} style={{ width:48,height:38,padding:4,cursor:"pointer" }} />
                  <input value={brand.primaryColor} onChange={e=>setBrand(p=>({...p,primaryColor:e.target.value}))} style={{ flex:1,fontFamily:"'IBM Plex Mono',monospace",fontSize:12 }} />
                </div>
              </Field>
              <Field label="Accent Color">
                <div style={{ display:"flex",gap:8,alignItems:"center" }}>
                  <input type="color" value={brand.accentColor} onChange={e=>setBrand(p=>({...p,accentColor:e.target.value}))} style={{ width:48,height:38,padding:4,cursor:"pointer" }} />
                  <input value={brand.accentColor} onChange={e=>setBrand(p=>({...p,accentColor:e.target.value}))} style={{ flex:1,fontFamily:"'IBM Plex Mono',monospace",fontSize:12 }} />
                </div>
              </Field>
            </div>
          </Card>

          <Card>
            <div style={{ fontWeight:700,fontSize:15,marginBottom:20 }}>Report Branding</div>
            <Field label="Report Header Title"><input value={brand.reportHeader} onChange={e=>setBrand(p=>({...p,reportHeader:e.target.value}))} /></Field>
            <Field label="Footer Text"><input value={brand.footerText} onChange={e=>setBrand(p=>({...p,footerText:e.target.value}))} /></Field>
            <label style={{ display:"flex",alignItems:"center",gap:10,marginBottom:20,cursor:"pointer" }}>
              <input type="checkbox" checked={brand.reportLogo} onChange={e=>setBrand(p=>({...p,reportLogo:e.target.checked}))} style={{ width:"auto",accentColor:C.accent }} />
              <span style={{ fontSize:13,color:C.textDim }}>Include company logo on all reports</span>
            </label>
            <Btn variant="primary" onClick={save} style={{ width:"100%" }}>{saved?"✓ Saved!":"Save Branding"}</Btn>
          </Card>
        </div>

        {/* Live preview */}
        <div>
          <Card>
            <div style={{ fontWeight:700,fontSize:14,marginBottom:16 }}>Live Preview</div>
            <div style={{ background:"#07090d",borderRadius:8,padding:20,border:`1px solid ${C.border}` }}>
              {/* Sidebar preview */}
              <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:16,paddingBottom:16,borderBottom:`1px solid ${C.border}` }}>
                <span style={{ fontSize:20,color:brand.primaryColor }}>{brand.logo}</span>
                <div>
                  <div style={{ fontWeight:900,fontSize:15,letterSpacing:2,color:brand.primaryColor }}>{brand.appName.toUpperCase()}</div>
                  <div style={{ fontSize:9,color:"#4a5878",letterSpacing:2 }}>{brand.tagline.toUpperCase()}</div>
                </div>
              </div>
              {["Dashboard","Scans","Targets","Reports"].map((item,i)=>(
                <div key={i} style={{ padding:"8px 10px",borderRadius:5,color:i===0?brand.primaryColor:"#7a90b0",marginBottom:4,background:i===0?brand.primaryColor+"15":"transparent",fontSize:13,fontWeight:600 }}>
                  {item}
                </div>
              ))}
            </div>

            <div style={{ marginTop:16,background:"#07090d",borderRadius:8,padding:20,border:`1px solid ${C.border}` }}>
              <div style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:10,color:"#4a5878",letterSpacing:2,marginBottom:6 }}>REPORT HEADER PREVIEW</div>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                <div>
                  <div style={{ fontSize:16,fontWeight:900,color:brand.primaryColor }}>{brand.appName}</div>
                  <div style={{ fontSize:11,color:"#7a90b0" }}>{brand.reportHeader}</div>
                </div>
                <div style={{ width:40,height:40,background:brand.primaryColor+"25",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,color:brand.primaryColor }}>{brand.logo}</div>
              </div>
              <div style={{ marginTop:12,fontSize:10,color:"#4a5878",borderTop:`1px solid #1a2035`,paddingTop:8 }}>{brand.footerText}</div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  SYSTEM
// ══════════════════════════════════════════════════════════════
function SystemPage({ toast }) {
  const services = [
    { name:"API Server",       status:"online",  uptime:"99.98%", latency:"12ms",  detail:"Express v4.18 · Port 3001" },
    { name:"Scan Worker",      status:"online",  uptime:"99.91%", latency:"—",     detail:"BullMQ · 3 concurrent jobs" },
    { name:"PostgreSQL",       status:"online",  uptime:"99.99%", latency:"3ms",   detail:"Supabase · 342MB used" },
    { name:"Redis Queue",      status:"online",  uptime:"99.95%", latency:"1ms",   detail:"Upstash · 0 pending jobs" },
    { name:"Email Service",    status:"online",  uptime:"99.87%", latency:"—",     detail:"SMTP · 1,240 sent this month" },
    { name:"PDF Generator",    status:"online",  uptime:"99.72%", latency:"—",     detail:"Puppeteer · Chromium 119" },
    { name:"PayFast Webhook",  status:"online",  uptime:"100%",   latency:"—",     detail:"ITN verified · Last hit 2h ago" },
    { name:"Nmap Engine",      status:"warning", uptime:"97.4%",  latency:"—",     detail:"v7.94 · 1 stuck job" },
  ];
  const statusC = { online:C.green, warning:C.warn, offline:C.critical };

  return (
    <div className="fade">
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontSize:24,fontWeight:800,marginBottom:4 }}>System Health</h1>
        <div style={{ fontSize:13,color:C.textMuted }}>Infrastructure status and configuration</div>
      </div>

      <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:24 }}>
        <StatCard label="Services Online" value="7/8"   color={C.green}  icon="⬡" />
        <StatCard label="Uptime (30d)"    value="99.8%" color={C.accent} icon="◎" />
        <StatCard label="Scans Today"     value="24"    color={C.gold}   icon="▦" />
        <StatCard label="Errors (24h)"    value="3"     color={C.warn}   icon="⚠" />
      </div>

      <Card style={{ marginBottom:16,padding:0,overflow:"hidden" }}>
        <table style={{ width:"100%",borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ background:C.surfaceAlt }}>
              {["Service","Status","Uptime","Latency","Details","Action"].map(h=>(
                <th key={h} style={{ padding:"10px 16px",fontSize:10,color:C.textMuted,fontFamily:"'IBM Plex Mono',monospace",letterSpacing:1,textAlign:"left",fontWeight:600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {services.map((s,i)=>(
              <tr key={i} style={{ borderBottom:`1px solid ${C.border}22` }}>
                <td style={{ padding:"12px 16px",fontWeight:600,fontSize:14 }}>{s.name}</td>
                <td style={{ padding:"12px 16px" }}>
                  <div style={{ display:"flex",alignItems:"center" }}><Dot color={statusC[s.status]} /><span style={{ color:statusC[s.status],fontSize:12,fontWeight:600,textTransform:"capitalize" }}>{s.status}</span></div>
                </td>
                <td style={{ padding:"12px 16px",fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:C.textDim }}>{s.uptime}</td>
                <td style={{ padding:"12px 16px",fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:C.accent }}>{s.latency}</td>
                <td style={{ padding:"12px 16px",fontSize:12,color:C.textMuted }}>{s.detail}</td>
                <td style={{ padding:"12px 16px" }}>
                  {s.status==="warning"&&<Btn variant="warn" size="sm" onClick={()=>toast(`Restarting ${s.name}...`)}>Restart</Btn>}
                  {s.status==="online" &&<Btn variant="ghost" size="sm">Logs</Btn>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:16 }}>
        <Card>
          <div style={{ fontWeight:700,fontSize:15,marginBottom:20 }}>Environment</div>
          {[["NODE_ENV","production"],["API_PORT","3001"],["DATABASE","Supabase · Connected"],["REDIS","Upstash · Connected"],["PAYFAST_MODE","Live"],["APP_URL","https://yourapp.co.za"]].map(([k,v])=>(
            <div key={k} style={{ display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${C.border}22`,fontSize:13 }}>
              <span style={{ color:C.textMuted,fontFamily:"'IBM Plex Mono',monospace",fontSize:11 }}>{k}</span>
              <span style={{ color:C.green,fontFamily:"'IBM Plex Mono',monospace",fontSize:11 }}>{v}</span>
            </div>
          ))}
        </Card>

        <Card>
          <div style={{ fontWeight:700,fontSize:15,marginBottom:20 }}>Quick Actions</div>
          <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
            {[
              ["Flush scan queue",    "danger",  "Clears all pending scans"],
              ["Send test email",     "ghost",   "Sends test to admin email"],
              ["Generate test report","ghost",   "Creates a sample PDF report"],
              ["Clear report cache",  "warn",    "Removes cached PDF files"],
              ["Check PayFast ITN",   "ghost",   "Verify webhook is reachable"],
            ].map(([label,variant,desc])=>(
              <div key={label} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${C.border}22` }}>
                <div>
                  <div style={{ fontSize:13,fontWeight:600,marginBottom:2 }}>{label}</div>
                  <div style={{ fontSize:11,color:C.textMuted }}>{desc}</div>
                </div>
                <Btn variant={variant} size="sm" onClick={()=>toast(`${label} executed`)}>{label.split(" ")[0]}</Btn>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  ROOT
// ══════════════════════════════════════════════════════════════
export default function AdminPanel() {
  const [page, setPage]       = useState("overview");
  const [clients, setClients] = useState([]);
  const [allScans, setAllScans] = useState([]);
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToastMsg]  = useState(null);

  const showToast = (msg, type="success") => { setToastMsg({ msg, type }); };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, clientsRes, scansRes] = await Promise.all([
        api("/api/admin/stats"),
        api("/api/admin/clients"),
        api("/api/admin/scans/all"),
      ]);
      if (!statsRes.error)   setStats(statsRes);
      if (Array.isArray(clientsRes)) setClients(clientsRes.map(normalizeClient));
      if (Array.isArray(scansRes))   setAllScans(scansRes);
    } catch (e) {
      console.error("Admin data load error:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const updateClientStatus = async (id, status) => {
    await api(`/api/admin/clients/${id}/status`, {
      method: "PUT",
      body: JSON.stringify({ status }),
    });
    setClients(p => p.map(c => c.id === id ? { ...c, status } : c));
    showToast(`Status updated to ${status}`);
  };

  const sendResetEmail = async (c) => {
    await api(`/api/admin/clients/${c.id}/reset-password`, { method: "POST" });
    showToast(`Password reset email sent to ${c.email}`);
  };

  return (
    <>
      <style>{G}</style>
      {toast && <Toast {...toast} onClose={()=>setToastMsg(null)} />}

      <div style={{ display:"flex",minHeight:"100vh" }}>
        {/* Sidebar */}
        <div style={{ width:220,background:C.surface,borderRight:`1px solid ${C.border}`,display:"flex",flexDirection:"column",position:"fixed",height:"100vh",zIndex:20 }}>
          <div style={{ padding:"18px 16px",borderBottom:`1px solid ${C.border}` }}>
            <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:4 }}>
              <span style={{ fontSize:18,color:C.accent }}>⬡</span>
              <span style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:14,fontWeight:600,letterSpacing:1,color:C.accent }}>SENTINEL</span>
            </div>
            <div style={{ fontSize:10,color:C.textMuted,fontFamily:"'IBM Plex Mono',monospace",letterSpacing:2,marginLeft:26 }}>ADMIN CONSOLE</div>
          </div>

          <nav style={{ flex:1,padding:"10px 10px",overflowY:"auto" }}>
            {ADMIN_NAV.map(item=>(
              <button key={item.id} onClick={()=>setPage(item.id)} style={{
                width:"100%",display:"flex",alignItems:"center",gap:9,padding:"9px 11px",
                background:page===item.id?C.accentDim:"transparent",
                border:`1px solid ${page===item.id?C.accent+"30":C.border+"00"}`,
                borderRadius:6,color:page===item.id?C.accent:C.textMuted,
                cursor:"pointer",fontSize:13,fontFamily:"'Outfit',sans-serif",fontWeight:600,
                marginBottom:2,transition:"all .15s",textAlign:"left",
              }}>
                <span style={{ fontSize:13,opacity:.85 }}>{item.icon}</span>{item.label}
              </button>
            ))}
          </nav>

          <div style={{ padding:"12px 10px",borderTop:`1px solid ${C.border}` }}>
            <div style={{ background:C.surfaceAlt,borderRadius:8,padding:"10px 12px",marginBottom:10 }}>
              <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                <Avatar name="Admin" size={28} />
                <div>
                  <div style={{ fontSize:12,fontWeight:700 }}>Admin</div>
                  <div style={{ fontSize:10,color:C.textMuted }}>Super Admin</div>
                </div>
              </div>
            </div>
            <div style={{ display:"flex",gap:6 }}>
              <Pill color={loading?C.textMuted:C.green}>{loading?"Loading…":"System OK"}</Pill>
              <Pill color={C.gold}>{clients.filter(c=>c.status==="active").length} active</Pill>
            </div>
          </div>
        </div>

        {/* Main */}
        <div style={{ marginLeft:220,flex:1,padding:"28px 32px",minHeight:"100vh" }}>
          {page==="overview" && <Overview clients={clients} stats={stats} onNav={setPage} />}
          {page==="clients"  && <Clients  clients={clients} setClients={setClients} toast={showToast} updateClientStatus={updateClientStatus} sendResetEmail={sendResetEmail} />}
          {page==="scans"    && <ScanMonitor allScans={allScans} loading={loading} />}
          {page==="revenue"  && <Revenue clients={clients} stats={stats} />}
          {page==="plans"    && <PlansManager toast={showToast} />}
          {page==="branding" && <WhiteLabel  toast={showToast} />}
          {page==="system"   && <SystemPage  toast={showToast} />}
        </div>
      </div>
    </>
  );
}
